/**
 * @fileoverview Componente responsável por reatribuir conexões do WhatsApp.
 */

import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      minWidth: 400,
    },
  },
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: "100%",
  },
  errorIcon: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(1),
  },
  warningBox: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light + "20",
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.error.light}`,
  },
  whatsappOption: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  }
}));

const ReassignWhatsAppModal = ({ open, onClose, ticketId, ticketStatus, onSuccess }) => {
  const classes = useStyles();
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applyToAll, setApplyToAll] = useState(false);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (!open) return;

    const fetchWhatsapps = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/whatsapp", {
          params: { companyId: user.companyId }
        });

        // Filtrar apenas conexões ativas
        const activeWhatsapps = data.filter(w => w.status === "CONNECTED");
        setWhatsapps(activeWhatsapps);

        // Se houver apenas uma conexão ativa, selecioná-la automaticamente
        if (activeWhatsapps.length === 1) {
          setSelectedWhatsappId(activeWhatsapps[0].id);
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchWhatsapps();
  }, [open, user.companyId]);

  const handleReassign = async () => {
    if (!selectedWhatsappId) {
      toast.error("Por favor, selecione uma conexão");
      return;
    }

    setSubmitting(true);
    try {
      if (applyToAll && ticketStatus) {
        // Reassociar em massa para todos os tickets órfãos no status atual
        const result = await api.post('/tickets/bulk-reassign-whatsapp', {
          whatsappId: selectedWhatsappId,
          status: ticketStatus
        });

        toast.success(`${result.data.updatedCount} conversas reassociadas com sucesso!`);
      } else {
        // Reassociar apenas o ticket atual
        const { data } = await api.put(`/tickets/${ticketId}/reassign-whatsapp`, {
          whatsappId: selectedWhatsappId
        });

        toast.success("Conversa reassociada com sucesso!");

        // Chamar callback com o ticket atualizado
        if (onSuccess && data.ticket) {
          onSuccess(data.ticket);
        }
      }

      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedWhatsappId("");
      onClose();
    }
  };

  const handleDialogClose = (_event, reason) => {
    if (submitting && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      className={classes.root}
    >
      <DialogTitle>
        Alterar conexão do WhatsApp
      </DialogTitle>

      <DialogContent>
        <Box className={classes.warningBox}>
          <ErrorOutlineIcon className={classes.errorIcon} />
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Esta conversa não tem uma conexão
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Selecione uma conexão para continuar conversando.
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress />
          </Box>
        ) : whatsapps.length === 0 ? (
          <Typography color="error" align="center">
            Nenhuma conexão WhatsApp ativa disponível.
            Por favor, verifique suas conexões.
          </Typography>
        ) : (
          <FormControl className={classes.formControl}>
            <InputLabel id="whatsapp-select-label">
              Selecione uma conexão
            </InputLabel>
            <Select
              labelId="whatsapp-select-label"
              value={selectedWhatsappId}
              onChange={(e) => setSelectedWhatsappId(e.target.value)}
              disabled={submitting}
            >
              <MenuItem value="">
                <em>Selecione...</em>
              </MenuItem>
              {whatsapps.map((whatsapp) => (
                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                  <Box className={classes.whatsappOption}>
                    <WhatsAppIcon fontSize="small" />
                    <span>{whatsapp.name}</span>
                    {whatsapp.isDefault && (
                      <Typography variant="caption" color="primary">
                        (Padrão)
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedWhatsappId && ticketStatus && (
          <Box style={{ marginTop: 16 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToAll}
                  onChange={(e) => setApplyToAll(e.target.checked)}
                  disabled={submitting}
                  color="primary"
                />
              }
              label="Aplicar a todos sem conexão"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleReassign}
          color="primary"
          variant="contained"
          disabled={!selectedWhatsappId || submitting || loading}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              Reassociando...
            </>
          ) : (
            "Reassociar"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReassignWhatsAppModal;
